<!-- 农业管理首页 -->
<template>
    <MainLayout class="main-layout" style="height: calc(100% - 16px); padding: 0px 8px" rightWidth="320px"
        @toggleRight="handleToggleRight">
        <News slot="header"> </News>
        <!-- 左侧 -->
        <LeftAside slot="left" :treeData="plotTree" :activeId="activeFieldId" @node-click="handleNodeClick"
            @select="handleFieldSelect" @search="handleSearch" @delete="handleDelete" @edit="handleEdit"
            @load-children="handleLoadChildren"></LeftAside>
        <!-- 中间地图 -->
        <div ref="middlRef" slot="main" class="c-middle" style="padding: 12px">
            <MapContainer ref="mapRef" :useServiceConfigZoom="false" :bottomToolConfig="{ show: false }" :zoom="13">
                <template slot="openlayer">
                    <!-- 机库图层：复用主页面 HivesPoints，点击弹机库状态弹窗；showDiversionDrop 强制显示备降点图层。先绘制（底层） -->
                    <HivesPoints :hives="siteList" :showDiversionDrop="true"></HivesPoints>
                    <!-- 地块图层：绘制在上层，避免被机库图标盖住地块多边形 -->
                    <MapLayer :fields="fieldList" :waterPoints="waterPointList" :takeoffPoints="takeoffPointList"
                        :showAddress="false" @field-click="handleFieldClick"></MapLayer>
                </template>
            </MapContainer>

            <!-- 地块详情弹窗 -->
            <FieldInfoDialog :visible.sync="dialogVisible" :plotId="currentPlotId" @view-source="handleViewSource"
                @view-photos="handleViewPhotos" @close="dialogVisible = false" />
        </div>
        <Right slot="right" :rightShow="rightShow" :overviewList="overviewList" :cropList="cropList"
            :plantingList="plantingList" @handleDateChange="handleDateChange" @crop-change="handleCropChange"></Right>
    </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout';
import News from '@/page/mainPage/components/news/News.vue';
import MapContainer from '@/components/map/MapContainer/MapContainer.vue';
import LeftAside from './components/LeftAside.vue';
import MapLayer from './components/MapLayer.vue';
import Right from './components/Right.vue';
import FieldInfoDialog from './components/FieldInfoDialog.vue';
import HivesPoints from '@/page/mainPage/mapComponents/HivesPoints.vue';
import NongyeApi from '../api';
import UtilS from '@/utils/MapUtil';
import DockApi from '@/api/flight/dock';

// 机库站点列表轮询间隔（毫秒）：30 秒刷新一次，兼顾数据新鲜度与请求频率
const SITE_POLL_INTERVAL = 30 * 1000;

export default {
    components: {
        MainLayout,
        News,
        MapContainer,
        LeftAside,
        MapLayer,
        Right,
        FieldInfoDialog,
        HivesPoints,
    },
    data () {
        return {
            rightShow: false,
            activeFieldId: null,
            plotTree: [],
            plotList: [],
            waterPointList: [],
            takeoffPointList: [],
            // 机库站点列表（接口数据，喂给 HivesPoints）
            siteList: [],
            overviewList: [],
            cropList: [],
            plantingList: [],
            dialogVisible: false,
            currentPlotId: null,
            // 左侧树当前选中的区域 id，作为统计接口的 areaId 检索字段
            selectedAreaId: null,
            selectedCropId: null, // 当前选中的作物 id（统计检索用）
        };
    },
    computed: {
        fieldList () {
            const list = [];
            const walk = (nodes) => {
                if (!Array.isArray(nodes)) return;
                nodes.forEach((node) => {
                    if (node && node.nodeType === 'plot') {
                        const plant = Array.isArray(node.plantList) && node.plantList.length ? node.plantList[0] : {};
                        list.push({
                            id: node.id,
                            name: node.name,
                            plotCode: node.plotCode,
                            plotName: node.plotName,
                            area: node.area,
                            landType: node.landType,
                            cropName: plant.cropName || node.landType,
                            cropImgUrl: plant.cropImgUrl || '',
                            centerLng: node.centerLng,
                            centerLat: node.centerLat,
                            address: node.address,
                            plantDate: plant.plantingDate || '',
                            harvestDate: plant.expectedHarvestDate || '',
                            status: node.status,
                            plantList: node.plantList || [],
                            coordinates: this.parseWkt(node.boundary),
                        });
                    }
                    if (node && node.children) walk(node.children);
                });
            };
            walk(this.plotTree);
            return list;
        },
    },
    created () {
    },
    mounted () {
        this.getPlotTree();
        this.getSiteList();
        this.startSitePoll();
    },
    beforeDestroy () {
        this.stopSitePoll();
    },
    methods: {
        // 切换右侧图表显示
        handleDateChange (val) {
            this.loadStatistics(val);
        },
        // 加载统计信息
        async loadStatistics (val) {
            let params = { ...(val || {}) };
            // 选中区域后按 areaIds 过滤统计
            if (this.selectedAreaId) {
                params.areaIds = this.selectedAreaId;
            }
            // 选中作物后按 cropId 过滤统计
            if (this.selectedCropId) {
                params.cropId = this.selectedCropId;
            }
            console.log('params', params);
            try {
                const [totalRes, areaRes, statusRes] = await Promise.all([
                    NongyeApi.countAgriPlot(params),
                    NongyeApi.countPlantArea(params),
                    NongyeApi.countPlantStatus(params),
                ]);
                const total = totalRes?.data || totalRes || {};
                const cropColors = ['#49f0fd', '#ef8200', '#7df5b0', '#a48bff', '#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff'];
                const statusIcons = [
                    { icon: 'icon-plant-1', bg: 'rgba(73, 240, 253, 0.3)' },
                    { icon: 'icon-plant-2', bg: 'rgba(239, 130, 0, 0.3)' },
                    { icon: 'icon-plant-3', bg: 'rgba(167, 139, 250, 0.3)' },
                    { icon: 'icon-plant-4', bg: 'rgba(125, 245, 176, 0.3)' },
                ];
                this.overviewList = [
                    { url: 'icon-ny-1', name: this.$t('nongye.indexPage.plotCount'), unit: this.$t('nongye.indexPage.countUnit'), value: total.plotCount ?? 0 },
                    { url: 'icon-ny-2', name: this.$t('nongye.indexPage.plantArea'), unit: this.$t('nongye.indexPage.mu'), value: total.plantArea ?? 0 },
                    { url: 'icon-ny-3', name: this.$t('nongye.indexPage.farmCount'), unit: this.$t('nongye.indexPage.onceUnit'), value: total.farmCount ?? 0 },
                    { url: 'icon-ny-4', name: this.$t('nongye.indexPage.farmArea'), unit: this.$t('nongye.indexPage.mu'), value: total.farmArea ?? 0 },
                    { url: 'icon-ny-5', name: this.$t('nongye.indexPage.serviceCount'), unit: this.$t('nongye.indexPage.orderUnit'), value: total.serviceCount ?? 0 },
                    { url: 'icon-ny-6', name: this.$t('nongye.indexPage.flightCount'), unit: this.$t('nongye.indexPage.onceUnit'), value: total.flightCount ?? 0 },
                ];
                const areaList = areaRes?.rows || areaRes?.data || (Array.isArray(areaRes) ? areaRes : []);
                this.cropList = (Array.isArray(areaList) ? areaList : []).map((item, idx) => ({
                    name: item.name,
                    code: item.code,
                    value: item.value ?? 0,
                    unit: this.$t('nongye.indexPage.mu'),
                    color: cropColors[idx % cropColors.length],
                }));
                const statusList = statusRes?.rows || statusRes?.data || (Array.isArray(statusRes) ? statusRes : []);
                this.plantingList = (Array.isArray(statusList) ? statusList : []).map((item, idx) => {
                    const cfg = statusIcons[idx % statusIcons.length];
                    return {
                        name: item.name,
                        code: item.code,
                        value: item.value ?? 0,
                        unit: this.$t('nongye.indexPage.countUnit'),
                        label: item.name,
                        icon: cfg.icon,
                        bg: cfg.bg,
                    };
                });
            } catch (e) {
                console.error('[Nongye Index] loadStatistics 异常', e);
            }
            this.waterPointList = [
                { id: 'w1', name: '北侧取水口', lon: 113.270, lat: 23.142 },
                { id: 'w2', name: '南侧取水口', lon: 113.268, lat: 23.118 },
            ];
            this.takeoffPointList = [
                { id: 't1', name: '机库A', lon: 113.273, lat: 23.130 },
                { id: 't2', name: '机库B', lon: 113.262, lat: 23.140 },
            ];
        },
        // 解析WKT字符串为多边形坐标
        parseWkt (boundary) {
            if (!boundary) return [];
            try {
                if (typeof UtilS?.WKTtoParsePolygon === 'function') {
                    const parsed = UtilS.WKTtoParsePolygon(boundary);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return [parsed];
                    }
                }
            } catch (e) {
                // ignore
            }
            return [];
        },
        // 获取机库站点列表（来自 routePage 站点接口）。showLog=false 静默拉取（定时器用）
        getSiteList (showLog = false) {
            DockApi.fetchSiteList().then((res) => {
                const list = (res && res.code === 200 && Array.isArray(res.rows)) ? res.rows : [];
                this.siteList = list;
            }).catch((e) => {
                // 定时器轮询失败时仅警告，不清空现有数据避免地图点位闪烁
                if (showLog) {
                    console.error('[Nongye Index] getSiteList 初始加载失败', e);
                } else {
                    console.warn('[Nongye Index] getSiteList 轮询失败', e);
                }
            });
        },
        // 启动机库站点列表轮询（30s 一次）。重复启动前先清理，防止组件热更新/复用时叠加定时器
        startSitePoll () {
            this.stopSitePoll();
            this.$options.sitePollTimer = setInterval(() => {
                this.getSiteList(false);
            }, SITE_POLL_INTERVAL);
        },
        stopSitePoll () {
            if (this.$options.sitePollTimer) {
                clearInterval(this.$options.sitePollTimer);
                this.$options.sitePollTimer = null;
            }
        },
        // 获取地块树
        async getPlotTree (val) {
            try {
                const res = await NongyeApi.listAgriPlotTreeBs({
                    codeOrName: val || '',
                });
                const raw = res?.data || res?.rows || res || [];
                // 过滤掉 "nodeType === 'area' 且无 children" 的孤立区域节点
                this.plotTree = this.filterEmptyAreas(raw);
                console.log('this.plotTree', this.plotTree);
            } catch (e) {
                this.$message.error(this.$t('nongye.indexPage.fetchPlotTreeFailed'));
                this.plotTree = [];
            }
        },
        // 过滤没有 children 的 area 节点（递归）
        filterEmptyAreas(nodes) {
            if (!Array.isArray(nodes)) return [];
            return nodes
                .map((n) => {
                    if (!n) return null;
                    // 递归先处理子树
                    if (Array.isArray(n.children)) {
                        n.children = this.filterEmptyAreas(n.children);
                    }
                    return n;
                })
                .filter((n) => {
                    if (!n) return false;
                    // 隐藏规则：nodeType === 'area' 且 children 已加载但为空数组。
                    // 注意：children 为 undefined 表示尚未懒加载，必须保留，否则节点永远没有机会加载子节点
                    if (n.nodeType === 'area' && Array.isArray(n.children) && n.children.length === 0) {
                        return false;
                    }
                    return true;
                });
        },
        // 递归把指定 areaId 的子节点合并到 plotTree 中
        mergeAreaChildren(areaId, areaPayload) {
            // 从接口返回值中安全地解析出"该 area 的直接子节点"
            const children = this.extractAreaChildren(areaId, areaPayload);
            if (!Array.isArray(children)) {
                console.warn('[mergeAreaChildren] 无法解析 area 子节点，放弃合并', areaId);
                return;
            }
            const walk = (list) => {
                if (!Array.isArray(list)) return false;
                for (let i = 0; i < list.length; i += 1) {
                    const n = list[i];
                    // 必须 id 与 nodeType 同时匹配，避免挂错同名节点
                    if (n && n.id === areaId && n.nodeType === 'area') {
                        if (!Array.isArray(n.children)) {
                            if (children.length === 0) {
                                // 空数据区域：直接从树上移除，保持隐藏
                                list.splice(i, 1);
                            } else {
                                this.$set(n, 'children', children);
                            }
                        }
                        return true;
                    }
                    if (n.children && walk(n.children)) return true;
                }
                return false;
            };
            walk(this.plotTree);
        },
        // 安全提取 area 的子节点：
        // 1) 若返回的是单棵树 (root.id === areaId)，取其 children
        // 2) 若返回的是节点数组，过滤出 parentId === areaId 的项
        // 3) 若都不匹配，返回 null（不合并，避免破坏树结构）
        extractAreaChildren(areaId, payload) {
            if (!payload) return null;
            const arr = Array.isArray(payload) ? payload : [payload];
            // 形式 1：返回的是带根节点的整棵树
            const root = arr.find((x) => x && x.id === areaId);
            if (root && Array.isArray(root.children)) {
                return root.children;
            }
            // 形式 2：返回的是节点数组，每项带 parentId
            const direct = arr.filter((x) => x && String(x.parentId) === String(areaId));
            if (direct.length) return direct;
            // 都不匹配：不合并
            return null;
        },
        // 处理 LeftAside 的懒加载请求
        async handleLoadChildren(areaIds) {
            if (!Array.isArray(areaIds) || !areaIds.length) return;
            // 串行处理，避免请求风暴
            // eslint-disable-next-line no-restricted-syntax
            for (const areaId of areaIds) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const res = await NongyeApi.treeAgriPlot({ areaId });
                    // 兼容多种返回形态（data / rows / 直接数组 / 单根节点）
                    const payload = res?.data ?? res?.rows ?? res;
                    this.mergeAreaChildren(areaId, payload);
                } catch (e) {
                    console.error('[handleLoadChildren] error', areaId, e);
                }
            }
        },
        // 点击地块
        handleFieldClick (field) {
            this.currentPlotId = field.id;
            this.dialogVisible = true;
        },
        // 选择地块
        handleFieldSelect (item) {
            this.activeFieldId = item.id;
            this.$message.success(this.$t('nongye.indexPage.selectedPlot', { name: item.plotName || item.name }));
            // 根据地块边界计算 extent，让地图自动适配并完整显示
            if (!this.$refs.mapRef) return;
            const extent = this.computeFieldExtent(item);
            if (extent) {
                this.$refs.mapRef.fitExtent(extent, { padding: [40, 40, 40, 40], maxZoom: 19, duration: 500 });
                return;
            }
            // 没有边界数据时降级：根据中心点和面积估算一个合适的缩放级别
            const lng = Number(item.centerLng);
            const lat = Number(item.centerLat);
            if (Number.isFinite(lng) && Number.isFinite(lat)) {
                const area = Number(item.area);
                this.$refs.mapRef.toCenter([lng, lat]);
                if (this.$refs.mapRef.toMapZoom) {
                    // area 单位为亩，简单换算后给一个随面积递减的 zoom（面积越大层级越低）
                    const zoom = Number.isFinite(area) && area > 0
                        ? Math.max(13, Math.min(19, Math.round(20 - Math.log2(area + 1))))
                        : 17;
                    this.$refs.mapRef.toMapZoom(zoom);
                }
            }
        },
        // 计算地块的经纬度边界 [minLng, minLat, maxLng, maxLat]
        computeFieldExtent (item) {
            // 优先使用 fieldList 中已解析的 coordinates
            const field = this.fieldList.find((f) => String(f.id) === String(item.id));
            let coords = field && Array.isArray(field.coordinates) ? field.coordinates : null;
            if (coords && coords.length && Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
                // coordinates 形如 [[ [lng,lat], [lng,lat], ... ]]，取首层
                coords = coords[0];
            }
            if (!Array.isArray(coords) || coords.length === 0) return null;
            let minLng = Infinity; let minLat = Infinity; let maxLng = -Infinity; let
                maxLat = -Infinity;
            coords.forEach((pt) => {
                if (!Array.isArray(pt) || pt.length < 2) return;
                const x = Number(pt[0]);
                const y = Number(pt[1]);
                if (!Number.isFinite(x) || !Number.isFinite(y)) return;
                if (x < minLng) minLng = x;
                if (x > maxLng) maxLng = x;
                if (y < minLat) minLat = y;
                if (y > maxLat) maxLat = y;
            });
            if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null;
            return [minLng, minLat, maxLng, maxLat];
        },
        // 点击节点
        handleNodeClick (data) {
            if (data && data.nodeType === 'area') {
                // 点击区域：选中该区域并刷新统计；再次点击已选中的区域则取消，恢复全局统计
                this.selectedAreaId = this.selectedAreaId === data.id ? null : data.id;
                this.loadStatistics();
                return;
            }
            this.activeFieldId = data.id;
        },
        // 选中作物下拉框：记录 cropId 并刷新统计；清空则恢复不按作物过滤
        handleCropChange (cropId) {
            this.selectedCropId = cropId;
            this.loadStatistics();
        },
        // 搜索地块
        async handleSearch (params) {
            try {
                console.log('handleSearch', params);
                this.getPlotTree(params?.keyword || '');
            } catch (e) {
                this.$message.error(this.$t('nongye.indexPage.searchPlotFailed'));
            }
        },
        // 删除地块
        async handleDelete (data) {
            await this.getPlotTree();
            if (this.activeFieldId === data.id) this.activeFieldId = null;
        },
        // 编辑地块
        handleEdit (data) {
            this.$message.info(this.$t('nongye.indexPage.editPlot', { name: data.plotName || data.name }));
        },
        // 切换右侧图表显示
        handleToggleRight (visible) {
            this.rightShow = visible;
        },
        /**
         * 处理查看溯源码事件
         * @param {Object|string} payload - 地块信息，支持对象形式{ id }或直接传id值
         */
        handleViewSource (payload) {
            const id = payload?.id || payload;
            this.$message.info(this.$t('nongye.indexPage.viewTraceCode', { id }));
        },
        // 查看照片
        handleViewPhotos (payload) {
            const id = payload?.id || payload;
            this.$message.info(this.$t('nongye.indexPage.viewPhoto', { id }));
        },
    },
};
</script>

<style lang="scss" scoped>
.main-layout {
    height: calc(100% - 16px);
    padding: 0px 8px;
}

.c-middle {
    width: 100%;
    height: 100%;
}
</style>
